import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { sendContactMessage } from "@/lib/contact.functions";
import { MapPin, Phone, Mail, ArrowRight, Car, X } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { kontaktFaq, kontaktSections } from "@/data/hub-content";

const searchSchema = z.object({
  fordon: z.string().optional(),
  id: z.string().optional(),
});

export const Route = createFileRoute("/kontakt")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(kontaktFaq) }),
  validateSearch: (s) => searchSchema.parse(s),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Kontakt – AK Husbil & Fritid, Stabsvägen 5 Kalix" },
      {
        name: "description",
        content:
          "Ring Aaron på 070-862 97 43 eller besök oss på Stabsvägen 5 i Kalix. Svar inom 24 timmar — fordon, inbyte och finansiering.",
      },
      { property: "og:title", content: "Kontakta AK Husbil & Fritid" },
      {
        property: "og:description",
        content: "Stabsvägen 5, Kalix · 070-862 97 43 · Svar inom 24 timmar.",
      },
      { property: "og:url", content: "https://www.akhusbilfritid.se/kontakt" },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { property: "og:image:alt", content: "Kontakta AK Husbil & Fritid i Kalix" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://www.akhusbilfritid.se/kontakt" },
      { rel: "alternate", hrefLang: "fi-FI", href: "https://www.akhusbilfritid.se/fi/yhteystiedot" },
      { rel: "alternate", hrefLang: "sv-SE", href: "https://www.akhusbilfritid.se/kontakt" },
      { rel: "alternate", hrefLang: "x-default", href: "https://www.akhusbilfritid.se/kontakt" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Kontakta AK Husbil & Fritid",
          url: "/kontakt",
          mainEntity: { "@id": "#organization" },
        }),
      },
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
    ],
  }),
  component: KontaktPage,
});

const schema = z.object({
  name: z.string().trim().min(1, "Ange ditt namn").max(200),
  email: z.string().trim().email("Ogiltig e-post").max(320),
  phone: z.string().trim().max(40).optional(),
  message: z.string().trim().min(1, "Skriv ett meddelande").max(5000),
});

function KontaktPage() {
  const send = useServerFn(sendContactMessage);
  const { fordon, id } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Live-tracked fields to know when to stop glowing.
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  // Prefill message when arriving from a vehicle card.
  useEffect(() => {
    if (fordon && !message) {
      const url = id ? `https://www.akhusbilfritid.se/fordon/${id}` : "";
      setMessage(
        `Hej!\n\nJag är intresserad av: ${fordon}${url ? `\n${url}` : ""}\n\nJag skulle vilja veta mer om:\n– Skick och service\n– Möjlighet till provkörning/visning\n– Finansiering och eventuellt inbyte\n\nHör gärna av dig så snart det passar.\n\nVänligen,\n`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fordon, id]);

  const clearVehicle = () => {
    setMessage("");
    navigate({ search: {}, replace: true });
  };

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched({ name: true, email: true, message: true });
    const raw = {
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      message: message.trim(),
    };
    const parsed = schema.safeParse(raw);
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const i of parsed.error.issues) errs[String(i.path[0])] = i.message;
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await send({ data: parsed.data });
      toast.success("Tack! Vi hör av oss så snart vi kan.");
      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
      setTouched({});
      if (fordon) navigate({ search: {}, replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Något gick fel");
    } finally {
      setSubmitting(false);
    }
  }

  const isEmpty = (v: string) => v.trim().length === 0;
  const canSend = !isEmpty(name) && !isEmpty(email) && !isEmpty(message);

  return (
    <>
      <PageHeader
        label="Kontakt · Svar inom 24 timmar"
        title={<>Vi finns här för <span className="font-italic-display text-accent">dig</span>.</>}
        sub="Frågor om ett specifikt fordon, inbyte eller finansiering? Ring, mejla eller skriv här — du får alltid ett ärligt svar från en av oss. Välkommen även in på Stabsvägen 5."
      />

      <section className="container-edge pb-24">
        <div className="grid gap-10 md:grid-cols-12">
          {/* Info column */}
          <div className="md:col-span-5">
            <div className="space-y-3">
              <InfoBlock label="Besök oss" icon={<MapPin size={20} />} primary="Stabsvägen 5, Kalix" />
              <InfoBlock
                label="Säljare · Aaron"
                icon={<Phone size={20} />}
                primary={<a href="tel:0708629743" className="hover:text-accent">070-862 97 43</a>}
              />
              <InfoBlock label="E-post" icon={<Mail size={20} />} primary="Skicka meddelande →" />
              <div className="rounded-2xl border border-border bg-card p-5">
                <p className="text-xs font-medium uppercase tracking-wider text-accent">Öppettider</p>
                <p className="mt-3 font-display text-lg">Enligt överenskommelse</p>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  Ring <a href="tel:0708629743" className="text-foreground hover:text-accent">070-862 97 43</a> så bokar vi tid — även kvällar och helger går bra.
                </p>
              </div>
            </div>

            <div className="mt-6 overflow-hidden rounded-3xl border border-border soft-shadow">
              <iframe
                title="Karta"
                src="https://www.google.com/maps?q=Stabsv%C3%A4gen+5,+Kalix&output=embed"
                className="h-72 w-full"
                loading="lazy"
              />
            </div>
          </div>

          {/* Form column */}
          <div className="md:col-span-7">
            <form onSubmit={onSubmit} className="rounded-3xl border border-border bg-card p-8 md:p-10 elev-shadow">
              <p className="eyebrow">Skicka meddelande</p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl">
                Skriv några rader — vi <span className="font-italic-display text-accent">återkommer</span>.
              </h2>

              {fordon && (
                <div className="mt-6 flex items-start justify-between gap-3 rounded-2xl border border-accent/40 bg-accent/5 p-4">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent/15 text-accent">
                      <Car size={16} />
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-accent">Om detta fordon</p>
                      <p className="mt-0.5 text-sm font-semibold text-foreground">{fordon}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Fordonet är förifyllt i meddelandet. Fyll i namn, e-post och ev. telefon nedan så återkommer vi.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clearVehicle}
                    aria-label="Ta bort fordon"
                    className="rounded-full p-1 text-muted-foreground hover:bg-background hover:text-foreground"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              <div className="mt-8 space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field
                    label="Namn"
                    name="name"
                    value={name}
                    onChange={setName}
                    onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                    required
                    highlight={isEmpty(name) && (touched.name || !!fordon)}
                    error={errors.name}
                  />
                  <Field
                    label="Telefon"
                    name="phone"
                    type="tel"
                    value={phone}
                    onChange={setPhone}
                    error={errors.phone}
                  />
                </div>
                <Field
                  label="E-post"
                  name="email"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  required
                  highlight={isEmpty(email) && (touched.email || !!fordon)}
                  error={errors.email}
                />

                <div>
                  <label className="mb-2 block text-sm font-medium">
                    Meddelande <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    name="message"
                    rows={8}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, message: true }))}
                    className={`w-full rounded-2xl border bg-background px-4 py-3 text-base outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 ${
                      isEmpty(message) && (touched.message || !!fordon)
                        ? "border-accent ring-2 ring-accent/40 animate-pulse-glow"
                        : "border-input"
                    }`}
                  />
                  {errors.message && <p className="mt-1.5 text-xs text-destructive">{errors.message}</p>}
                </div>

                {!canSend && (
                  <p className="text-xs text-muted-foreground">
                    Fält som lyser behöver fyllas i innan meddelandet kan skickas.
                  </p>
                )}

                <button
                  disabled={submitting || !canSend}
                  className="group inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-6 py-3.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? "Skickar…" : "Skicka meddelande"}
                  <ArrowRight size={16} className="transition group-hover:translate-x-0.5" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <HubEditorial
        eyebrow="Praktisk information"
        sections={kontaktSections}
        faq={kontaktFaq}
        faqTitle="Vanliga frågor"
      />
    </>
  );
}

function InfoBlock({ label, icon, primary }: { label: string; icon: React.ReactNode; primary: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <p className="text-xs font-medium uppercase tracking-wider text-accent">{label}</p>
      <div className="mt-3 flex items-center gap-3 font-display text-xl md:text-2xl">
        <span className="text-accent">{icon}</span> {primary}
      </div>
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  value,
  onChange,
  onBlur,
  required,
  highlight,
  error,
}: {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  required?: boolean;
  highlight?: boolean;
  error?: string;
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      <input
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={`w-full rounded-full border bg-background px-5 py-3 text-base outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 ${
          highlight
            ? "border-accent ring-2 ring-accent/40 animate-pulse-glow"
            : "border-input"
        }`}
      />
      {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
    </div>
  );
}
